import * as amplitude from '@amplitude/analytics-browser';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';

import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { EventProvider } from 'hb-react/shared/apps/Events/contexts/EventContext';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import useNavigatorShare from 'hb-react/shared/hooks/useNavigatorShare';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import EventDisplay from './EventDisplay';

const UserEventModal = ({
  event,
  eventId,
  userId,
  onCloseModal,
  getActionsItems,
}) => {
  const [eventState, setEventState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEmpty(event)) {
      setIsLoading(true);
      axios
        .get(Routes.user_event_path(userId, eventId))
        .then(({ data }) => {
          setEventState(data?.event);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setEventState(event);
    }
  }, [eventId, userId, event]);

  const actionsItems = useMemo(() => {
    if (!getActionsItems) {
      return null;
    }

    return eventState ? getActionsItems(eventState) : [];
  }, [eventState]);

  const { handleShare, shareUrl } = useNavigatorShare({
    data: {
      text: eventState?.title,
      url: eventState?.id && Routes.event_preview_path(eventState.id),
    },
    onShare: () => {
      amplitude.track('Events|EventShared');
    },
  });

  if (handleShare) {
    const eventShareId = 'event-share-content';
    const isShareActionPresent = actionsItems?.some(
      (action) => action.id === eventShareId,
    );

    if (!isShareActionPresent) {
      const shareButtonProps = shareUrl && {
        as: 'a',
        href: shareUrl,
        'data-bypass-router': true,
      };

      actionsItems?.push({
        label: __('Share'),
        dataTestId: eventShareId,
        icon: () => (
          <FontAwesomeIcon
            iconName="share"
            iconType={CATEGORIES.SOLID}
            height={16}
          />
        ),
        onClick: handleShare,
        ...shareButtonProps,
      });
    }
  }

  return (
    <ModalWithTemplate
      onCloseModal={onCloseModal}
      header={eventState?.title}
      isFocusOnRef={false}
      isLoading={isEmpty(eventState) || isLoading}
      screenReadersCloseButtonText={s__(
        'UserEventAccessibility|Close the event %{eventName} window',
      ).i__({ eventName: eventState?.title || '' })}
    >
      {isEmpty(event) ? (
        <EventProvider event={eventState}>
          <EventDisplay
            isLoading={isEmpty(eventState)}
            actionsItems={actionsItems}
          />
        </EventProvider>
      ) : (
        <EventDisplay
          isLoading={isEmpty(eventState)}
          actionsItems={actionsItems}
        />
      )}
    </ModalWithTemplate>
  );
};

UserEventModal.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  event: PropTypes.object,
};

export default UserEventModal;
